(() => {
  if (customElements.get('storefront-context')) return;

  const storageKey = 'deqalli:storefront-context:v1';
  const changeEvent = 'storefront:context-change';
  const valid = (value) => value === 'heren' || value === 'dames';
  let privacyReady;
  let selectedContext = '';

  function publish(context) {
    const changed = document.documentElement.dataset.storefrontContext !== context;
    document.documentElement.dataset.storefrontContext = context;
    delete document.documentElement.dataset.storefrontContextPending;
    if (changed) document.dispatchEvent(new CustomEvent(changeEvent, { detail: { context } }));
  }

  function syncContext() {
    const config = document.querySelector('storefront-context');
    if (!config) return;
    const pageContext = valid(config.dataset.pageContext) ? config.dataset.pageContext : '';
    const defaultContext = valid(config.dataset.defaultContext) ? config.dataset.defaultContext : 'heren';
    let context = selectedContext || pageContext || defaultContext;

    if (!window.Shopify?.designMode) {
      let allowed = false;
      try {
        allowed = window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
        if (allowed) {
          if (selectedContext || pageContext) {
            sessionStorage.setItem(storageKey, context);
          } else {
            const saved = sessionStorage.getItem(storageKey);
            if (valid(saved)) context = saved;
          }
        } else {
          sessionStorage.removeItem(storageKey);
        }
      } catch {
        // The page's own context continues to work when storage or the privacy API is unavailable.
      }
    }
    publish(context);
  }

  function selectContext(context) {
    if (!valid(context)) return;
    selectedContext = context;
    syncContext();
  }

  function loadPrivacy() {
    if (!privacyReady) {
      privacyReady = new Promise((resolve) => {
        if (window.Shopify?.designMode || window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures) {
          resolve();
          return;
        }
        // A blocked privacy script must not leave the homepage concealed.
        const timeout = setTimeout(resolve, 1500);
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
          clearTimeout(timeout);
          resolve();
          syncContext();
        });
      });
    }
    return privacyReady;
  }

  customElements.define('storefront-context', class extends HTMLElement {
    connectedCallback() {
      if (window.Shopify?.designMode) selectedContext = '';
      loadPrivacy().then(syncContext, syncContext);
    }
  });

  const ready = loadPrivacy().then(syncContext, syncContext);
  window.DeqalliStorefrontContext = {
    ready,
    getContext: () => document.documentElement.dataset.storefrontContext || 'heren',
    selectContext,
  };

  // Keep inactive department markup inert, including its images and product controls.
  customElements.define('contextual-content', class extends HTMLElement {
    connectedCallback() {
      this.onContextChange = (event) => this.showContext(event.detail.context);
      document.addEventListener(changeEvent, this.onContextChange);
      ready.then(() => {
        if (this.isConnected) this.showContext(window.DeqalliStorefrontContext.getContext());
      });
    }

    disconnectedCallback() {
      document.removeEventListener(changeEvent, this.onContextChange);
    }

    showContext(context) {
      if (!valid(context) || this.dataset.context === context) return;
      const next = this.querySelector(`:scope > template[data-context-template="${context}"]`);
      const current = this.querySelector(':scope > [data-context-content]');
      if (!next || !current) return;
      const previous = document.createElement('template');
      previous.dataset.contextTemplate = this.dataset.context;
      current.replaceWith(next.content);
      previous.content.append(current);
      next.replaceWith(previous);
      this.dataset.context = context;
      this.querySelectorAll('slider-component, slideshow-component').forEach((slider) => slider.resetPages?.());
      window.initializeScrollAnimationTrigger?.(this);
    }
  });

  customElements.define('contextual-drawer', class extends HTMLElement {
    connectedCallback() {
      this.onContextChange = (event) => this.showContext(event.detail.context);
      this.onContextSelect = (event) => {
        const button = event.target.closest('button[data-storefront-context-select]');
        if (button) selectContext(button.dataset.storefrontContextSelect);
      };
      this.onBlockSelect = () => {
        if (!window.Shopify?.designMode) return;
        const drawer = this.closest('header-drawer');
        const details = drawer?.mainDetailsToggle;
        if (details && !details.open) {
          details.open = true;
          drawer.openMenuDrawer(details.querySelector('summary'));
        }
      };
      document.addEventListener(changeEvent, this.onContextChange);
      this.addEventListener('click', this.onContextSelect);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.showContext(document.documentElement.dataset.storefrontContext);
      this.querySelector('.menu-drawer__context-switch')?.removeAttribute('hidden');
    }

    disconnectedCallback() {
      document.removeEventListener(changeEvent, this.onContextChange);
      this.removeEventListener('click', this.onContextSelect);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    showContext(context) {
      if (!valid(context) || this.dataset.context === context) return;
      const next = this.querySelector(`template[data-context-template="${context}"]`);
      const current = this.querySelector('[data-context-content]');
      if (!next || !current) return;
      const focused = document.activeElement;
      const replacingFocus = current.contains(focused);
      const previous = document.createElement('template');
      previous.dataset.contextTemplate = this.dataset.context;
      current.querySelectorAll('details').forEach((details) => {
        details.open = false;
        details.classList.remove('menu-opening');
        details.querySelector('summary').setAttribute('aria-expanded', false);
      });
      current.classList.remove('submenu-open');
      current.querySelectorAll('.submenu-open').forEach((menu) => menu.classList.remove('submenu-open'));
      current.replaceWith(next.content);
      previous.content.append(current);
      next.replaceWith(previous);
      this.dataset.context = context;
      this.querySelector('.menu-drawer__context-switch')?.removeAttribute('hidden');

      const drawer = this.closest('header-drawer');
      drawer?.bindEvents();
      if (drawer?.mainDetailsToggle?.open && (replacingFocus || drawer.mainDetailsToggle.contains(focused))) {
        const selectedButton = this.querySelector(`[data-storefront-context-select="${context}"]`);
        trapFocus(drawer.mainDetailsToggle, replacingFocus ? selectedButton || drawer.mainDetailsToggle.querySelector('summary') : focused);
      }
    }
  });

  document.addEventListener('visitorConsentCollected', syncContext);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) loadPrivacy().then(syncContext, syncContext);
  });
})();
